import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import StateContext from 'common/contexts/state-context';
import MapContext from 'common/contexts/map-context';
import TrackingService from 'common/services/Tracking/Tracking';

import Breadcrumb from '../../ui/Breadcrumb';
import SalonHero from './SalonHero/SalonHero';
import PromoStrip from '../../ui/PromoStrip';
import Tabs from '../../ui/Tabs';
import CollectionSlider from '../../ui/CollectionSlider';
import ReassuranceBar from '../../ui/ReassuranceBar';
import LocatorMap from '../../ui/LocatorMap';
import PriceGrid from '../../ui/PriceGrid';
import ExpertiseGrid from '../../ui/ExpertiseGrid';
import GoodaysWidget from '../../ui/GoodaysWidget';
import ReviewGrid from '../../ui/ReviewGrid';
import SectionHead from '../../ui/SectionHead';
import JsonLd from '../../ui/JsonLd';
import { cityShortcutHref } from '../../ui/format';
import { USE_WP_CHROME } from '../../GlobalValues';

import { salonDescription, salonGalleryVariant } from '../../extendedFields';
import useRichFields from '../../richfields/useRichFields';
import { RICHFIELD_KEYS } from '../../richfields/keys';
import {
  toPromoStripProps,
  toPriceGridProps,
  toExpertiseGridProps,
  toCollectionSliderProps,
  toSalonGalleryProps,
  selectSalonSection,
} from '../../richfields/adapters';
// Contenus statiques : depuis la bascule richfield, `PROMO_STRIP`, `PRICES`,
// `EXPERTISES` et `COLLECTION_SLIDES` ne sont plus référencés que par les blocs
// JSX commentés (décision 12) — ils restent importés pour que revenir au
// statique ne soit qu'un décommentage. `SALON_GALLERY`, `SALON_REVIEWS` et
// `NETWORK_RATING`, eux, servent toujours.
/* eslint-disable no-unused-vars */
import {
  PROMO_STRIP,
  PRICES,
  EXPERTISES,
  COLLECTION_SLIDES,
  SALON_GALLERY,
  SALON_REVIEWS,
  NETWORK_RATING,
} from '../../staticContent';
/* eslint-enable no-unused-vars */

// Tirage pseudo-aléatoire à partir de `goodays.seed` (un flottant tiré une
// fois côté serveur par requête, cf. `services/react_manager.js`) plutôt que
// d'un `Math.random()` local : la page est rendue deux fois à partir du même
// `initialState` — une fois en SSR (`renderToString`), une fois à
// l'hydratation React côté client — et un tirage local donnerait un résultat
// différent à chaque passe, donc un mismatch d'hydratation. Réutiliser le
// même `seed` sur les deux passes garde le rendu identique tout en changeant
// à chaque nouvelle requête (donc à chaque rechargement de page). Le cap à 4
// reste ici (côté template) : d'autres templates ont leur propre logique
// d'affichage des avis Goodays (ex. `2025_VOG` en garde 3 via `.slice`).
const pickRandomReviews = (reviews, seed, count) => {
  if (reviews.length <= count) return reviews;
  return reviews
    .map((review, i) => ({ review, key: Math.sin(seed + i) }))
    .sort((a, b) => a.key - b.key)
    .slice(0, count)
    .map(({ review }) => review);
};

/**
 * Page détail d'un salon (§7.3 du plan). Chaque bloc éditable existe en double
 * (décision 12) : depuis le 02/09 c'est la version **richfield** qui est rendue,
 * la version statique restant écrite juste en dessous en commentaire — inverser
 * les deux suffit à revenir en arrière, sans toucher aux composants.
 *
 * Un groupe non saisi côté marque fait disparaître son bloc plutôt que de
 * retomber sur le statique : afficher des forfaits ou des expertises de
 * démonstration à la place de données absentes tromperait le visiteur. Seule la
 * galerie du hero garde un repli, la maquette n'ayant pas d'état sans photo.
 *
 * Les hooks richfield sont toujours appelés, jamais conditionnellement : c'est
 * ce qui permet de commuter un bloc sans jamais casser les règles des hooks.
 */
const LocalPage = () => {
  const [state] = useContext(StateContext);
  const [map, setMap] = useContext(MapContext);
  const { storeId } = useParams();

  // Chaque hook reste réellement appelé (jamais dans le bloc commenté) pour
  // que la commutation richfield ne casse jamais les règles des hooks ; sa
  // seule lecture, elle, est dans le JSX commenté juste sous son bloc statique.
  /* eslint-disable no-unused-vars */
  const rfSalon = useRichFields(RICHFIELD_KEYS.SALON);
  const rfPlanCanon = useRichFields(RICHFIELD_KEYS.PLAN_CANON);
  const rfTarifs = useRichFields(RICHFIELD_KEYS.TARIFS);
  const rfExpertises = useRichFields(RICHFIELD_KEYS.EXPERTISES);
  const rfCollection = useRichFields(RICHFIELD_KEYS.COLLECTION);
  /* eslint-enable no-unused-vars */

  useEffect(() => {
    if (storeId) TrackingService.pageView('localpage', storeId);
  }, [storeId]);

  // Recentrage de la carte sur le salon. `common` cadre sur `map.placeLoc` —
  // le même mécanisme que le listing après une recherche —, et une fiche salon
  // n'en a pas : on le pose depuis les coordonnées de la place. À faire une
  // fois l'API chargée, parce que `initGMap` réécrit `placeLoc` depuis les
  // réglages du storelocator (vides ici) au moment du `onGoogleApiLoaded`.
  const { latitude, longitude } = (state.lp && state.merchant) || {};
  useEffect(() => {
    if (!map.scriptInited || !latitude || !longitude) return;
    if (map.placeLoc.lat === latitude && map.placeLoc.lng === longitude) return;
    setMap((current) => ({
      ...current,
      placeId: undefined,
      placeLoc: { ...current.placeLoc, lat: latitude, lng: longitude },
    }));
  }, [map.scriptInited, map.placeLoc.lat, map.placeLoc.lng, latitude, longitude, setMap]);

  if (!state.lp) return null;

  const { merchant, goodays } = state;

  /* ── RICHFIELD ── (repli sur les photos statiques tant qu'aucune n'est saisie :
     la maquette n'a pas d'état « hero sans photo », la colonne resterait vide.
     `selectSalonSection` bascule sur la copie TCHIP Miami quand le champ de
     fusion 24 le désigne — voir `richfields/adapters.js`.) */
  const rfGallery = toSalonGalleryProps(selectSalonSection(rfSalon, salonGalleryVariant(merchant)));
  const galleryPhotos = rfGallery && rfGallery.photos.length ? rfGallery.photos : SALON_GALLERY;
  /* ── STATIQUE ──
  const galleryPhotos = SALON_GALLERY;
  */

  // Page ville du fil d'ariane (spec 3.a) — voir `cityShortcutHref`.
  const cityHref = cityShortcutHref(merchant.address.city, state.lp.search_shortcuts);

  // Origine absolue pour le `BreadcrumbList` JSON-LD (audit SEO §19.1) :
  // `cityHref`/`'/'` sont relatifs, or Google résout `itemListElement.item`
  // comme une URL absolue et ignore/rejette un chemin relatif.
  // `merchant.localpageUrl` ne suffit pas ici : il n'est renseigné que pour
  // lier vers *d'autres* salons (listing), pas vers la fiche courante — vérifié
  // vide en direct sur la fiche elle-même. `state.canonicalOrigin` (nouveau,
  // `services/react_helper.js`) porte la même origine que le lien `canonical`
  // déjà servi pour cette page, donc toujours définie pour une vraie requête.
  const breadcrumbOrigin = state.canonicalOrigin || '';

  // Note et avis : la note publique du salon et son nombre total d'avis
  // viennent de l'API Goodays (`/stats/google_score`, injectée côté serveur).
  // Les deux valent `null` quand Goodays ne connaît pas ce salon — les widgets
  // masquent alors la note, plutôt que d'afficher l'agrégat réseau à la place.
  //
  // Attention à ne pas confondre `reviewCount` et `reviews.length` : le premier
  // est le total réel du salon, le second le nombre d'avis effectivement
  // affichés (limités aux 5 étoiles avec message, cf. `goodays_manager`).
  const rating = goodays.rating;
  const reviewCount = goodays.reviewCount;
  const reviews = pickRandomReviews(goodays.reviews, goodays.seed || 0, 4);
  const hasReviews = reviews.length > 0;

  // AggregateRating + Review[] JSON-LD (plan §13 Phase 3) — mêmes données que
  // l'onglet Avis, jamais le widget Goodays embarqué.
  //
  // Pas de `@type`/`name` ici (décision Romain, 02/09) : `services/react_helper.js`
  // (`_schemaCreator`) injecte déjà côté serveur un premier `<script
  // type="application/ld+json">` `@type: HairSalon` avec nom/adresse/géo/horaires
  // (depuis `Schema.json`), avant même React. Réaffirmer `@type`/`name` ici
  // aurait créé deux entités `HairSalon` distinctes sur la même page plutôt
  // qu'une seule enrichie de ses avis — aucun champ note/avis n'existe côté
  // serveur, ce bloc reste donc nécessaire, mais réduit à l'agrégat.
  // `aggregateRating` n'est émis que si le salon a une note réelle : un
  // agrégat inventé (ou repris du réseau) sur une fiche salon est une donnée
  // structurée fausse, sanctionnable côté Google.
  const reviewsJsonLd = {
    '@context': 'https://schema.org',
    ...(rating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        bestRating: 5,
        reviewCount: reviewCount,
      },
    }),
    review: reviews.map((review) => ({
      '@type': 'Review',
      author: review.sender,
      reviewBody: review.content,
      reviewRating: { '@type': 'Rating', ratingValue: review.rating, bestRating: 5 },
    })),
  };

  return (
    <article itemScope itemType="https://schema.org/HairSalon">
      <JsonLd data={reviewsJsonLd} />
      <Breadcrumb
        items={[
          { label: 'Accueil', href: `${breadcrumbOrigin}/` },
          // Spec 3.a — niveau 2 : la page ville quand elle existe, le listing
          // général sinon. Le libellé suit la cible : annoncer « Salon de
          // coiffure Paris » pour renvoyer sur le listing général promettait une
          // page qui n'existe pas, et poussait la même URL dans le
          // `BreadcrumbList` JSON-LD sous un nom qui n'est pas le sien.
          cityHref
            ? { label: `Salon de coiffure ${merchant.address.city}`, href: `${breadcrumbOrigin}${cityHref}` }
            : { label: 'Tous nos salons', href: `${breadcrumbOrigin}/` },
          { label: merchant.name },
        ]}
      />

      {/* `hasReviews` gouverne à la fois l'onglet Avis et le lien rapide qui y
          mène : sans lui, un salon sans avis Goodays gardait un « Nos avis »
          pointant vers un onglet qui n'existe pas. */}
      <SalonHero
        photos={galleryPhotos}
        goodaysRating={rating}
        goodaysReviewCount={reviewCount}
        hasReviews={hasReviews}
      />

      {/* ── RICHFIELD ── */}
      <PromoStrip {...toPromoStripProps(rfPlanCanon[0])} />
      {/* ── STATIQUE ──
      <PromoStrip {...PROMO_STRIP} />
      */}

      <section className="salon-tabs" id="salon-tabs" style={{ padding: '64px 0 104px' }}>
        <div className="container">
          <Tabs
            ariaLabel="Informations du salon"
            initialTab="tarifs"
            scrollTargetId="salon-tabs"
            tabs={[
              {
                id: 'tarifs',
                label: 'Tarifs',
                content: (
                  <>
                    {/* ── RICHFIELD ── */}
                    {/* Pas d'eyebrow ici : la maquette n'en affiche un que sur le
                        listing (PricesWidget), pas dans cet onglet de la fiche salon.
                        `toPriceGridProps` n'en produit pas — le surtitre serait le
                        texte du <h2> répété juste au-dessus de lui. */}
                    <PriceGrid {...toPriceGridProps(rfTarifs[0])} withSection={false} centered={false} headingLevel="h2" />
                    {/* ── STATIQUE ──
                    <PriceGrid {...PRICES} eyebrow={undefined} withSection={false} centered={false} headingLevel="h2" />
                    */}
                  </>
                ),
              },
              hasReviews && {
                id: 'avis',
                label: 'Avis',
                content: (
                  <>
                    <SectionHead
                      title={{ line1: "Ce qu'elles", highlight: 'en pensent.' }}
                      subtitle={SALON_REVIEWS.subtitle}
                    />
                    {/* Goodays connaît le salon et sert ses avis sans forcément avoir
                        de note Google pour lui (~14 % du réseau TCHIP : la fiche est
                        absente de `/stats/google_score`). `GoodaysWidget` renvoie alors
                        `null`, mais `.note-score` est une carte blanche bordée et ombrée :
                        rendue à vide, elle laisse un cadre fantôme. On n'ouvre donc la
                        colonne que s'il y a une note à y mettre. */}
                    <div className={rating ? 'tab-avis-grid' : 'tab-avis-grid no-score'}>
                      {rating ? (
                        <div className="note-score">
                          <GoodaysWidget
                            variant="hero"
                            label={NETWORK_RATING.label}
                            value={rating}
                            reviewCount={reviewCount}
                          />
                        </div>
                      ) : null}
                      <ReviewGrid reviews={reviews} />
                    </div>
                  </>
                ),
              },
              {
                id: 'expertises',
                label: 'Expertises',
                content: (
                  <>
                    {/* ── RICHFIELD ── */}
                    <ExpertiseGrid {...toExpertiseGridProps(rfExpertises[0])} headingLevel="h2" />
                    {/* ── STATIQUE ──
                    <ExpertiseGrid {...EXPERTISES} headingLevel="h2" />
                    */}
                  </>
                ),
              },
              {
                id: 'infos',
                label: 'Infos pratiques',
                content: (
                  <>
                    <SectionHead
                      title={{ line1: 'Infos', highlight: 'pratiques' }}
                      // `salonDescription` : convention partagée VOG + TCHIP legacy
                      // (`merchant.description || extended_string_data_7`, §18 du
                      // plan) — le texte générique n'intervient qu'en dernier
                      // recours, si aucun des deux n'est renseigné.
                      subtitle={
                        salonDescription(merchant) ||
                        `Sans rendez-vous, suivez votre place depuis l'appli Tchip. ${merchant.formattedAddress}.`
                      }
                      // Retour client : cette description doit occuper toute la
                      // largeur de la page, contrairement aux autres `SectionHead`
                      // qui partagent le plafond `.section-sub { max-width: 540px }`.
                      className="infos-desc"
                    />
                    <LocatorMap
                      title={`Carte du salon ${merchant.name}`}
                      className="infos-map"
                      merchants={[merchant]}
                    />

                    {/* ── SALONS À PROXIMITÉ (décision 11 — ISO strict, en sommeil) ──
                    <NearbyMerchants merchants={merchant.closeMerchants} />
                    */}
                  </>
                ),
              },
            ].filter(Boolean)}
          />
        </div>
      </section>

      {/* ── RICHFIELD ── */}
      <CollectionSlider {...toCollectionSliderProps(rfCollection[0])} />
      {/* ── STATIQUE ──
      <CollectionSlider {...COLLECTION_SLIDES} />
      */}

      {/* Le footer WordPress porte déjà la réassurance (`.site-rass`) : on ne
          rend la version React que quand le chrome React est actif. */}
      {!USE_WP_CHROME && <ReassuranceBar />}
    </article>
  );
};

export default LocalPage;
